import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import DashIcon from '../../components/ui/DashIcon'
import DashAvatar from '../../components/ui/DashAvatar'
import StatusBadge from '../../components/ui/StatusBadge'
import ActionsMenu from '../../components/ui/ActionsMenu'
import AddCommandeModal from '../../components/expediteur/AddCommandeModal'
import { ImportCSVModal } from '../../components/expediteur/ImportCSVModal'
import { ExpCancelModal } from '../../components/expediteur/modals/ExpCancelModal'
import { useDeliveries } from '../../hooks/useDeliveries'
import type { Delivery } from '../../types'

const CANCELLABLE_STATUSES = ['pending', 'matching', 'validated']

const TAB_STATUS_MAP: Record<string, string | undefined> = {
  all:         undefined,
  en_cours:    'pending,validated,accepted,assigned,picked_up,in_transit',
  a_recuperer: 'accepted',
  livres:      'delivered',
  annules:     'cancelled,failed,returned',
}

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })

function EmptyState({ search, activeTab }: { search: string; activeTab: string }) {
  return (
    <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--muted)' }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>
        {search ? '🔍' : activeTab === 'livres' ? '✅' : '📦'}
      </div>
      <div style={{ fontWeight: 600, fontSize: '.9rem', color: 'var(--navy-800)', marginBottom: 6 }}>
        {search
          ? `Aucun résultat pour "${search}"`
          : activeTab === 'livres'
            ? 'Aucune livraison effectuée'
            : 'Aucun colis pour ce filtre'}
      </div>
      <div style={{ fontSize: '.82rem' }}>
        {search
          ? 'Essayez avec un autre nom, numéro ou adresse.'
          : 'Créez votre premier colis avec le bouton "+ Nouveau colis".'}
      </div>
    </div>
  )
}

export default function ExpediteurDeliveries() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState('all')
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [page, setPage] = useState(1)
  const [modalOpen, setModalOpen] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const [sel, setSel] = useState<Set<string>>(new Set())
  const [cancelTarget, setCancelTarget] = useState<Delivery | null>(null)

  // Debounce du champ de recherche
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 500)
    return () => clearTimeout(t)
  }, [search])

  // Réinitialiser la page quand les filtres changent
  useEffect(() => { setPage(1) }, [activeTab, debouncedSearch])

  const { data, isLoading, isFetching } = useDeliveries({
    status:  TAB_STATUS_MAP[activeTab],
    search:  debouncedSearch || undefined,
    page,
    limit:   20,
  })

  const deliveries: Delivery[] = data?.data ?? []
  const totalPages = data?.totalPages ?? 0
  const total = data?.total ?? 0

  function toggle(id: string) {
    const next = new Set(sel)
    if (next.has(id)) next.delete(id); else next.add(id)
    setSel(next)
  }

  const tabs = [
    { id: 'all',         label: 'Tous',       count: activeTab === 'all' ? total : undefined },
    { id: 'en_cours',    label: 'En cours',   count: activeTab === 'en_cours' ? total : undefined },
    { id: 'a_recuperer', label: 'À récupérer', count: activeTab === 'a_recuperer' ? total : undefined },
    { id: 'livres',      label: 'Livrés',     count: activeTab === 'livres' ? total : undefined },
    { id: 'annules',     label: 'Annulés',    count: activeTab === 'annules' ? total : undefined },
  ]

  return (
    <>
      {modalOpen && <AddCommandeModal onClose={() => setModalOpen(false)} />}
      <ImportCSVModal
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        onSuccess={(count) => {
          toast.success(`${count} colis importés avec succès !`)
          queryClient.invalidateQueries({ queryKey: ['deliveries'] })
        }}
      />
      {cancelTarget && (
        <ExpCancelModal
          deliveryId={cancelTarget._id}
          trackingCode={cancelTarget.trackingCode}
          onClose={() => setCancelTarget(null)}
          onSuccess={() => queryClient.invalidateQueries({ queryKey: ['deliveries'] })}
        />
      )}

      <div className="page-head">
        <div className="ph-titles">
          <h1>Colis</h1>
          <p>Gérez toutes vos expéditions en un seul endroit.</p>
        </div>
        <div className="ph-actions">
          <button className="btn btn-ghost" onClick={() => setImportOpen(true)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 6 }}>
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="12" y1="18" x2="12" y2="12"/>
              <line x1="9" y1="15" x2="15" y2="15"/>
            </svg>
            Importer CSV
          </button>
          <button className="btn btn-ghost"><DashIcon name="download" size={14} /> Exporter</button>
          <button className="btn btn-primary" onClick={() => setModalOpen(true)}>
            <DashIcon name="plus" size={14} /> Nouveau colis
          </button>
        </div>
      </div>

      <div className="tbl-card">
        <div className="tbl-toolbar">
          <div className="tbl-tabs">
            {tabs.map(t => (
              <button
                key={t.id}
                className={`tbl-tab${activeTab === t.id ? ' is-active' : ''}`}
                onClick={() => { setActiveTab(t.id); setSel(new Set()) }}
              >
                {t.label}
                {t.count !== undefined && <span className="count">{t.count}</span>}
              </button>
            ))}
          </div>
          <div style={{ flex: 1 }} />
          <div className="tbl-search">
            <DashIcon name="search" size={14} />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="ID colis, client, adresse…"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px', color: 'var(--muted)' }}
              >
                ×
              </button>
            )}
          </div>
        </div>

        {/* Indicateur de chargement en arrière-plan */}
        {isFetching && !isLoading && (
          <div style={{ height: 2, background: '#1D9E75', borderRadius: 0, transition: 'opacity 0.2s' }} />
        )}

        {sel.size > 0 && (
          <div style={{
            padding: '10px 16px', background: 'var(--green-50)',
            borderBottom: '1px solid var(--green-100)',
            display: 'flex', alignItems: 'center', gap: 14, fontSize: '.86rem',
          }}>
            <b style={{ color: 'var(--green-700)' }}>{sel.size} colis sélectionnés</b>
            <div style={{ flex: 1 }} />
            <button className="btn btn-sm btn-ghost">Imprimer étiquettes</button>
            <button className="btn btn-sm btn-ghost">Demander pickup</button>
            <button className="btn btn-sm btn-ghost" onClick={() => setSel(new Set())}>Annuler</button>
          </div>
        )}

        {isLoading ? (
          <div style={{ padding: '40px', color: 'var(--muted)', fontSize: '.82rem', textAlign: 'center' }}>
            Chargement…
          </div>
        ) : deliveries.length === 0 ? (
          <EmptyState search={debouncedSearch} activeTab={activeTab} />
        ) : (
          <div className="tbl-scroll">
            <table className="tbl">
              <thead>
                <tr>
                  <th style={{ width: 36 }}>
                    <input
                      type="checkbox"
                      checked={sel.size === deliveries.length && deliveries.length > 0}
                      onChange={e => setSel(e.target.checked ? new Set(deliveries.map(d => d._id)) : new Set())}
                    />
                  </th>
                  <th>ID</th>
                  <th>Destinataire</th>
                  <th>Adresse</th>
                  <th>Statut</th>
                  <th>Livreur</th>
                  <th>Créé</th>
                  <th>COD</th>
                  <th className="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {deliveries.map(d => (
                  <tr key={d._id} className={sel.has(d._id) ? 'is-selected' : ''}>
                    <td><input type="checkbox" checked={sel.has(d._id)} onChange={() => toggle(d._id)} /></td>
                    <td><span className="row-id">{d.trackingCode}</span></td>
                    <td>
                      <div className="cell-user">
                        <DashAvatar name={d.dropoff.contactName ?? '?'} size={28} />
                        <div>
                          <b>{d.dropoff.contactName ?? '—'}</b>
                          <span>{d.dropoff.contactPhone ?? ''}</span>
                        </div>
                      </div>
                    </td>
                    <td style={{ maxWidth: 220 }}>
                      <div style={{ color: 'var(--navy-900)', fontWeight: 500, fontSize: '.86rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {d.dropoff.address}
                      </div>
                    </td>
                    <td><StatusBadge status={d.status} /></td>
                    <td>
                      {d.livreur?.name
                        ? <div className="cell-user"><DashAvatar name={d.livreur.name} size={24} /><span style={{ fontSize: '.82rem', color: 'var(--navy-800)' }}>{d.livreur.name.split(' ')[0]}</span></div>
                        : <span style={{ color: 'var(--muted)', fontSize: '.82rem' }}>—</span>}
                    </td>
                    <td className="mono" style={{ color: 'var(--muted)' }}>{formatDate(d.createdAt)}</td>
                    <td className="mono row-strong">{d.price?.base ?? 0} TND</td>
                    <td className="col-actions">
                      <button onClick={() => navigate(`/expediteur/colis/${d._id}`)}>Détails</button>
                      <ActionsMenu
                        actions={[
                          { label: 'Voir les détails', icon: 'package', onClick: () => navigate(`/expediteur/colis/${d._id}`) },
                          ...(CANCELLABLE_STATUSES.includes(d.status)
                            ? [{ label: 'Annuler le colis', icon: 'x', danger: true, onClick: () => setCancelTarget(d) }]
                            : []),
                        ]}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        <div style={{ padding: '14px 18px', borderTop: '1px solid var(--line)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '.82rem', color: 'var(--muted)' }}>
          <span>
            {total > 0
              ? `Affichage ${(page - 1) * 20 + 1}–${Math.min(page * 20, total)} sur ${total}`
              : 'Aucun résultat'}
          </span>
          {totalPages > 1 && (
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                className="btn btn-sm btn-ghost"
                disabled={page === 1}
                onClick={() => { setPage(p => p - 1); window.scrollTo(0, 0) }}
              >←</button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                const p = i + 1
                return (
                  <button
                    key={p}
                    className={`btn btn-sm ${p === page ? 'btn-primary' : 'btn-ghost'}`}
                    onClick={() => { setPage(p); window.scrollTo(0, 0) }}
                  >
                    {p}
                  </button>
                )
              })}
              {totalPages > 5 && <span style={{ alignSelf: 'center' }}>…</span>}
              {totalPages > 5 && (
                <button
                  className={`btn btn-sm ${totalPages === page ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => { setPage(totalPages); window.scrollTo(0, 0) }}
                >
                  {totalPages}
                </button>
              )}
              <button
                className="btn btn-sm btn-ghost"
                disabled={page === totalPages}
                onClick={() => { setPage(p => p + 1); window.scrollTo(0, 0) }}
              >→</button>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
